import React from 'react';

import Link from 'next/link';

import { Divider } from '@repo/editor/src/components/utilities/styles';
import { pluralSuffix } from '@repo/editor/src/utils';
import { withEventScope } from '@repo/event-processor';
import { Badge } from '@repo/ui/components/Badge';
import { primaryTheme } from '@repo/ui/components/Buttons';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2 } from '@repo/ui/components/Typography';
import { Modal } from '@repo/ui/deprecated/Modal';

import LinkButton from 'components/LinkButton/LinkButton';
import {
  announcementStoreSelector,
  useAnnouncementStore,
} from 'stores/announcementStore/announcementStore';
import { FlexColumn } from 'styles/layouts';

import { Image, Description, Video, Wrapper, TextWrapper } from './styles';

const AnnouncementModal: React.FC = () => {
  const items = useAnnouncementStore(announcementStoreSelector.items);
  const badge = useAnnouncementStore(announcementStoreSelector.badge);
  const isOpen = useAnnouncementStore(announcementStoreSelector.isOpen);
  const closeModal = useAnnouncementStore(announcementStoreSelector.closeModal);
  const markAsRead = useAnnouncementStore(announcementStoreSelector.markAsRead);

  const handleClick = (): void => {
    void markAsRead();

    if (items?.length === 1) {
      closeModal();
    }
  };

  const handleClose = (): void => {
    closeModal();

    void markAsRead();
  };

  const target = items?.length !== 1 ? '_blank' : undefined;

  return (
    <Modal
      consistentVerticalScrollbar
      externalCloseButton
      isOpen={isOpen}
      width="816px"
      onClose={handleClose}
    >
      <Wrapper>
        <FlexColumn alignItems="center" width="100%">
          <Badge
            size="s"
            text={badge ?? `New Feature${pluralSuffix(items?.length ?? 0)}`}
            variant="blue"
          />
          <Spacer height="12px" />
          {items?.map((item, index, array) => (
            <FlexColumn key={item.key} alignItems="center" width="100%">
              <TextWrapper>
                <H2>{item.title}</H2>
                <Spacer height="24px" />
                <Description>{item.description}</Description>
                <Spacer height="24px" />
              </TextWrapper>
              <LinkButton
                href={item.link}
                label={item.buttonText}
                target={target}
                theme={primaryTheme}
                width="260px"
                onClick={handleClick}
              />
              <Spacer height="24px" />
              {item.video && (
                <Link href={item.link} target={target} onClick={handleClick}>
                  <Video autoPlay loop muted playsInline>
                    <source src={item.video} type="video/mp4" />
                  </Video>
                </Link>
              )}
              {item.image && (
                <Link href={item.link} target={target} onClick={handleClick}>
                  <Image
                    alt={item.imageAlt ?? 'Announcement image'}
                    src={item.image}
                  />
                </Link>
              )}
              {item.offer && (
                <>
                  <Spacer height="24px" />
                  <Description>{item.offer.text}</Description>
                </>
              )}
              {index !== array.length - 1 && (
                <Divider style={{ margin: '36px 0' }} />
              )}
            </FlexColumn>
          ))}
        </FlexColumn>
      </Wrapper>
    </Modal>
  );
};

export default withEventScope(AnnouncementModal, {
  feature: 'announcementModal',
});
